import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronRight, GitCompare, History, X } from 'lucide-react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const statusStyle = {
  pending_review: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
};

const VersionHistory = ({ itemId, canReview, initialVersionId, onApplied }) => {
  const [expanded, setExpanded] = useState(Boolean(initialVersionId));
  const [versions, setVersions] = useState([]);
  const [selectedId, setSelectedId] = useState(initialVersionId || null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [note, setNote] = useState('');

  const loadVersions = async (preferredId = selectedId) => {
    if (!itemId) return;
    try {
      const response = await axiosInstance.get(`/resolutions/${itemId}/versions`);
      const items = response.data.versions || [];
      setVersions(items);
      if (!preferredId || !items.some(item => item.id === preferredId)) {
        const pending = items.find(item => item.status === 'pending_review');
        setSelectedId(pending?.id || items[0]?.id || null);
      }
    } catch {
      setVersions([]);
    }
  };

  useEffect(() => {
    setSelectedId(initialVersionId || null);
    setSelected(null);
    setExpanded(Boolean(initialVersionId));
    loadVersions(initialVersionId || null);
  }, [itemId, initialVersionId]);

  useEffect(() => {
    if (!expanded || !selectedId) return;
    setLoading(true);
    axiosInstance.get(`/resolutions/${itemId}/versions/${selectedId}`)
      .then(response => setSelected(response.data))
      .catch(() => toast.error('Could not load this version'))
      .finally(() => setLoading(false));
  }, [expanded, itemId, selectedId]);

  const review = async (action) => {
    if (!selectedId) return;
    setReviewing(true);
    try {
      await axiosInstance.post(
        `/resolutions/${itemId}/versions/${selectedId}/${action}`,
        { note }
      );
      toast.success(action === 'approve' ? 'New source version approved and indexed' : 'Proposed version rejected');
      setNote('');
      await loadVersions(selectedId);
      const refreshed = await axiosInstance.get(`/resolutions/${itemId}/versions/${selectedId}`);
      setSelected(refreshed.data);
      onApplied?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not ${action} this version`);
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="version-history">
      <button
        type="button"
        onClick={() => setExpanded(current => !current)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <History size={15} /> Version history and what changed
          {versions.some(version => version.status === 'pending_review') && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
              Review needed
            </span>
          )}
        </span>
        {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
      </button>

      {expanded && (
        <div className="grid gap-4 border-t p-4 lg:grid-cols-[220px_1fr]">
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {versions.length === 0 && <p className="text-xs text-gray-400">No saved versions yet.</p>}
            {versions.map((version, index) => (
              <button
                type="button"
                key={version.id}
                onClick={() => setSelectedId(version.id)}
                className={`w-full rounded-md border p-2 text-left text-xs ${selectedId === version.id ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <strong>{version.version_label || `Revision ${versions.length - index}`}</strong>
                  <span className={`rounded-full border px-1.5 py-0.5 text-[9px] ${statusStyle[version.status] || 'bg-gray-100 text-gray-600'}`}>
                    {(version.status || '').replaceAll('_', ' ')}
                  </span>
                </div>
                <p className="mt-1 text-gray-500">{new Date(version.created_at).toLocaleString()}</p>
                <p className="mt-1 line-clamp-2 text-gray-600">{version.comparison?.summary}</p>
              </button>
            ))}
          </div>

          <div className="min-w-0">
            {loading && <p className="text-sm text-gray-500">Loading comparison…</p>}
            {!loading && selected && (
              <>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                      <GitCompare size={14} /> What changed
                    </p>
                    <p className="text-xs text-gray-500">{selected.comparison?.summary}</p>
                  </div>
                  <span className={`rounded-full border px-2 py-1 text-[10px] font-semibold ${statusStyle[selected.status] || 'bg-gray-100 text-gray-600'}`}>
                    {(selected.status || '').replaceAll('_', ' ')}
                  </span>
                </div>

                {(selected.comparison?.metadata_changes || []).length > 0 && (
                  <div className="mb-3 space-y-1.5 rounded-md bg-slate-50 p-2">
                    {selected.comparison.metadata_changes.map(change => (
                      <div key={change.field} className="grid gap-1 text-[10px] text-slate-600 sm:grid-cols-[130px_1fr]">
                        <strong>{change.field.replaceAll('_', ' ')}</strong>
                        <span className="break-words">
                          {JSON.stringify(change.before ?? null)} → {JSON.stringify(change.after ?? null)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="max-h-80 overflow-auto rounded-md border bg-slate-950 font-mono text-[11px]">
                  {(selected.comparison?.lines || []).map((line, index) => (
                    <div
                      key={`${index}-${line.kind}`}
                      className={`grid grid-cols-[38px_38px_20px_1fr] gap-1 px-2 py-0.5 ${
                        line.kind === 'added'
                          ? 'bg-emerald-950 text-emerald-200'
                          : line.kind === 'removed'
                            ? 'bg-rose-950 text-rose-200'
                            : 'text-slate-400'
                      }`}
                    >
                      <span>{line.old_line || ''}</span>
                      <span>{line.new_line || ''}</span>
                      <span>{line.kind === 'added' ? '+' : line.kind === 'removed' ? '−' : ' '}</span>
                      <span className="whitespace-pre-wrap break-words">{line.text || ' '}</span>
                    </div>
                  ))}
                </div>

                {selected.status === 'pending_review' && canReview && (
                  <div className="mt-3 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs text-amber-900">
                      The current approved source remains searchable until you approve this version.
                    </p>
                    <Textarea
                      value={note}
                      onChange={event => setNote(event.target.value)}
                      rows={2}
                      placeholder="Optional review note"
                      className="bg-white text-xs"
                    />
                    <div className="flex justify-end gap-2">
                      <Button type="button" size="sm" variant="outline" disabled={reviewing} onClick={() => review('reject')}>
                        <X size={13} className="mr-1" /> Reject
                      </Button>
                      <Button type="button" size="sm" disabled={reviewing} onClick={() => review('approve')}>
                        <Check size={13} className="mr-1" /> Approve and index
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionHistory;
